import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMonth } from '../context/MonthContext';
import { getMonthName } from '../utils/format';
import { getAvatar, DEFAULT_AVATAR_ID } from '../utils/avatars';
import { useRefresh } from '../context/RefreshContext';
import Ajudante from './Ajudante';
import {
  Home,
  Wallet,
  Receipt,
  Target,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  LogOut,
  Settings,
  RotateCw
} from 'lucide-react';

function Layout({ children, showMonthSelector = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, increaseFontSize, decreaseFontSize } = useAuth();
  const { currentMonth, goToPreviousMonth, goToNextMonth, canGoPrevious, canGoNext } = useMonth();
  const { triggerRefresh, refreshing } = useRefresh();

  const navItems = [
    { path: '/painel', label: 'Painel', icon: Home },
    { path: '/receitas', label: 'Receitas', icon: Wallet },
    { path: '/despesas', label: 'Despesas', icon: Receipt },
    { path: '/metas', label: 'Metas', icon: Target },
    { path: '/relatorios', label: 'Relatórios', icon: BarChart3 },
    { path: '/ajustes', label: 'Ajustes', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  const firstName = user?.nome ? user.nome.split(' ')[0] : '';
  const today = new Date();
  const formattedDate = today
    .toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .replace(/^(\d+) de (\w)/, (_m, d, first) => `${d} de ${first.toUpperCase()}`);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header with app name and controls */}
      <header className="bg-[#1B2A4A] text-white py-3 px-3 sm:py-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-2 sm:gap-4">
          <h1 className="text-lg sm:text-xl font-bold truncate" data-testid="app-header">
            <span className="sm:hidden">Bolso</span>
            <span className="hidden sm:inline">Bolso Sob Controle</span>
          </h1>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0" data-testid="font-controls">
            <button
              onClick={decreaseFontSize}
              className="w-11 h-11 flex items-center justify-center bg-white/10 border border-white/30 rounded-lg font-bold text-white hover:bg-white/20 active:bg-white/30 transition-all"
              aria-label="Diminuir fonte"
              data-testid="font-decrease-btn"
            >
              <span className="text-base">A</span>
              <Minus size={12} />
            </button>
            <button
              onClick={increaseFontSize}
              className="w-11 h-11 flex items-center justify-center bg-white/10 border border-white/30 rounded-lg font-bold text-white hover:bg-white/20 active:bg-white/30 transition-all"
              aria-label="Aumentar fonte"
              data-testid="font-increase-btn"
            >
              <span className="text-base">A</span>
              <Plus size={12} />
            </button>
            <button
              onClick={triggerRefresh}
              disabled={refreshing}
              className="w-11 h-11 flex items-center justify-center bg-white/10 border border-white/30 rounded-lg text-white hover:bg-white/20 active:bg-white/30 disabled:opacity-60 transition-all"
              aria-label="Atualizar"
              data-testid="refresh-btn"
            >
              <RotateCw size={18} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={logout}
              className="h-11 px-3 sm:px-4 flex items-center gap-2 bg-red-500/90 hover:bg-red-600 active:bg-red-700 text-white rounded-lg font-semibold shadow-sm transition-all"
              aria-label="Sair"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              <span className="text-sm">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Welcome / Date */}
      <div className="bg-white border-b border-slate-200" data-testid="welcome-bar">
        <div key={firstName || 'anon'} className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          {(() => {
            const avatar = getAvatar(user?.avatar || DEFAULT_AVATAR_ID);
            const AvatarIcon = avatar.Icon;
            return (
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 animate-welcome-avatar ${avatar.bg}`}>
                <AvatarIcon size={26} className={avatar.color} strokeWidth={1.75} />
              </div>
            );
          })()}
          <p className="text-lg md:text-xl font-semibold text-[#1B2A4A] animate-welcome-text">
            Bem-vindo{firstName ? `, ${firstName}` : ''}
            <span className="text-slate-500 font-normal"> — {formattedDate}</span>
          </p>
        </div>
      </div>

      {/* Month Selector */}
      {showMonthSelector && (
        <div className="bg-white border-b-2 border-slate-200 sticky top-0 z-40 shadow-sm" data-testid="month-selector">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-4 py-4 px-6">
            <button
              onClick={goToPreviousMonth}
              disabled={!canGoPrevious}
              className="w-14 h-14 flex items-center justify-center bg-[#1B2A4A] text-white rounded-xl hover:bg-[#2C3E50] transition-all active:scale-95 shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
              aria-label="Mês anterior"
              data-testid="prev-month-btn"
            >
              <ChevronLeft size={28} />
            </button>

            <div className="min-w-[200px] text-center" data-testid="current-month-display">
              <span className="text-2xl md:text-3xl font-bold text-[#1B2A4A]">
                {getMonthName(currentMonth.mes)} {currentMonth.ano}
              </span>
            </div>

            <button
              onClick={goToNextMonth}
              disabled={!canGoNext}
              className="w-14 h-14 flex items-center justify-center bg-[#1B2A4A] text-white rounded-xl hover:bg-[#2C3E50] transition-all active:scale-95 shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
              aria-label="Próximo mês"
              data-testid="next-month-btn"
            >
              <ChevronRight size={28} />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="app-content max-w-4xl mx-auto px-4 md:px-6 py-6 pb-28">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-200 z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]" data-testid="bottom-nav">
        <div className="max-w-4xl mx-auto flex justify-around items-center h-20">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all min-w-[64px] ${
                  active 
                    ? 'bg-[#1B2A4A] text-white' 
                    : 'text-[#1B2A4A] hover:bg-slate-100'
                }`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <Icon size={24} className="mb-1" />
                <span className="text-xs font-semibold">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <Ajudante />
    </div>
  );
}

export default Layout;
